/**
 * PlanillasEmbed — embed del sistema externo K360 (payroll) dentro de
 * Finance 360 vía iframe. La URL se configura en Configuración → Empresa →
 * Integraciones y es per-emisor.
 *
 * Visible solo si:
 *   - El emisor tiene planillasEnabled = true.
 *   - El rol del usuario tiene permiso 'planillas'.
 * (Ambos checks viven en Layout.jsx para mostrar/ocultar el menú).
 */

import { useEffect, useState } from 'react';
import { Plug, AlertCircle, ExternalLink, RefreshCw, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { planillasApi } from '../../utils/api';

// Zoom del iframe — entre 60% y 100% en pasos de 5%. Persistido en localStorage.
const ZOOM_KEY = 'f360_planillas_zoom';
const ZOOM_MIN = 0.6;
const ZOOM_MAX = 1.0;
const ZOOM_STEP = 0.05;
const ZOOM_DEFAULT = 0.85;

export default function PlanillasEmbed() {
  const { currentEmisor } = useAuthStore();
  const planillasUrl = currentEmisor?.planillasUrl;
  const planillasEnabled = currentEmisor?.planillasEnabled;
  const ssoConfigured = !!currentEmisor?.planillasSsoConfigured;
  const [iframeKey, setIframeKey] = useState(0); // permite recargar el iframe
  const [loadStarted, setLoadStarted] = useState(false);
  // URL final del iframe — si SSO está configurado, se obtiene del backend con token firmado.
  // Si no, se usa planillasUrl directo (login manual).
  const [iframeSrc, setIframeSrc] = useState(null);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState(null);
  const [zoom, setZoom] = useState(() => {
    const saved = parseFloat(localStorage.getItem(ZOOM_KEY));
    return Number.isFinite(saved) && saved >= ZOOM_MIN && saved <= ZOOM_MAX ? saved : ZOOM_DEFAULT;
  });

  // Cuando cambia el emisor o se recarga manualmente (iframeKey),
  // si SSO está configurado pedimos una URL fresca al backend (el JWT expira en 60s).
  // Si no está configurado, usamos planillasUrl directo.
  useEffect(() => {
    if (!planillasEnabled || !planillasUrl) return;
    if (!ssoConfigured) {
      setIframeSrc(planillasUrl);
      setLoadStarted(true);
      return;
    }
    let cancelled = false;
    setSsoLoading(true);
    setSsoError(null);
    planillasApi.getSsoUrl()
      .then(res => {
        if (cancelled) return;
        const url = res.data?.data?.url;
        if (!url) throw new Error('No se recibió URL SSO');
        setIframeSrc(url);
        setLoadStarted(true);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[PlanillasEmbed] SSO error:', err);
        setSsoError(err.response?.data?.error || err.message || 'Error obteniendo URL SSO');
        // Fallback: cargar la URL sin SSO así el user al menos puede loguearse manualmente
        setIframeSrc(planillasUrl);
        setLoadStarted(true);
      })
      .finally(() => { if (!cancelled) setSsoLoading(false); });
    return () => { cancelled = true; };
  }, [currentEmisor?.id, planillasEnabled, planillasUrl, ssoConfigured, iframeKey]);

  useEffect(() => {
    localStorage.setItem(ZOOM_KEY, String(zoom));
  }, [zoom]);

  const zoomIn = () => setZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut = () => setZoom(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));

  // Caso: el toggle está apagado o no hay URL
  if (!planillasEnabled || !planillasUrl) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-20 px-6 text-center bg-white border border-gray-200 rounded-xl">
        <div className="p-4 rounded-full bg-violet-100 mb-4">
          <Plug className="h-10 w-10 text-violet-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Módulo Planillas no disponible</h2>
        <p className="text-sm text-gray-600 max-w-md">
          {!planillasEnabled
            ? 'El módulo Planillas no está activado para esta empresa.'
            : 'No se ha configurado la URL del sistema K360.'}
        </p>
        <p className="text-xs text-gray-500 mt-3 max-w-md">
          Andá a <strong>Configuración → Empresa → Integraciones</strong> para configurarlo.
        </p>
      </div>
    );
  }

  // Validación básica de URL (HTTPS recomendado)
  let urlValida;
  try {
    urlValida = new URL(planillasUrl);
  } catch {
    urlValida = null;
  }
  if (!urlValida) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-20 px-6 text-center bg-white border border-red-200 rounded-xl">
        <div className="p-4 rounded-full bg-red-100 mb-4">
          <AlertCircle className="h-10 w-10 text-red-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">URL inválida</h2>
        <p className="text-sm text-gray-600 max-w-md">
          La URL configurada para K360 no es válida: <code className="font-mono">{planillasUrl}</code>
        </p>
      </div>
    );
  }

  return (
    <div className="w-full relative bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm h-[calc(100dvh-130px)]">
      {/* Loading mientras se obtiene la URL SSO del backend */}
      {ssoLoading && !iframeSrc && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm z-20">
          <Loader2 className="h-8 w-8 text-violet-600 animate-spin mb-2" />
          <p className="text-sm text-gray-600">Iniciando sesión en Payroll360 Lite...</p>
        </div>
      )}
      {/* Aviso si el SSO falló pero se cargó URL como fallback */}
      {ssoError && (
        <div className="absolute top-3 left-3 z-20 px-3 py-2 bg-amber-50 border border-amber-300 rounded-md shadow text-xs text-amber-700 max-w-md">
          <strong>SSO no disponible:</strong> {ssoError}. Use el login manual.
        </div>
      )}

      {/* IFRAME a pantalla completa — sin header propio para maximizar altura.
          Se aplica zoom CSS para que el contenido de K360 se vea más compacto
          y entre todo en la pantalla sin tanto scroll.

          NOTA: NO usar `sandbox=` aquí. K360 es nuestro propio código confiable
          y el atributo sandbox bloquea el PDF viewer nativo de Chrome cuando se
          intenta cargar un PDF en iframes nested (modal "Vista previa" de
          Acciones de Personal, Liquidaciones, Constancias, etc). El error que da
          es "This page has been blocked by Chrome" SIN entrada en console — no
          hay token de sandbox que habilite plugins de PDF en nested iframes. */}
      {loadStarted && iframeSrc && (
        <iframe
          key={iframeKey}
          src={iframeSrc}
          title="Payroll360 Lite — Sistema de Planillas"
          className="w-full h-full border-0"
          style={{ zoom: zoom }}
          // Red local: K360 habla con el Firmador KuantaSign en http://127.0.0.1:3517. Chrome bloquea
          // que un iframe llegue a la red local si esta página no se lo delega (Local Network Access);
          // sin esto, dentro de Finance el firmador figura como «no instalado». Los nombres que el
          // navegador no conozca los ignora.
          allow="clipboard-read; clipboard-write; fullscreen; local-network-access; local-network; loopback-network"
        />
      )}

      {/* Mini-controles flotantes (bottom-right) — semi-transparentes, se acentúan en hover.
          Posicionados abajo para no chocar con el selector de empresa de K360 (top-right).
          No quitan espacio al iframe. Incluye zoom -/+, recargar, abrir aparte. */}
      <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1 opacity-40 hover:opacity-100 transition-opacity">
        <button
          onClick={zoomOut}
          disabled={zoom <= ZOOM_MIN}
          className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          title="Reducir tamaño"
        >
          <ZoomOut className="h-3.5 w-3.5 text-gray-600" />
        </button>
        <span
          className="px-1.5 py-0.5 bg-white border border-gray-200 rounded-md shadow-sm text-[10px] font-medium text-gray-600 cursor-pointer hover:bg-gray-50 select-none min-w-[36px] text-center"
          title="Click para restaurar 85%"
          onClick={() => setZoom(ZOOM_DEFAULT)}
        >
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={zoomIn}
          disabled={zoom >= ZOOM_MAX}
          className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          title="Aumentar tamaño"
        >
          <ZoomIn className="h-3.5 w-3.5 text-gray-600" />
        </button>
        <button
          onClick={() => setIframeKey(k => k + 1)}
          className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 ml-1"
          title="Recargar Payroll360 Lite"
        >
          <RefreshCw className="h-3.5 w-3.5 text-gray-600" />
        </button>
        <a
          href={planillasUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="p-1.5 bg-white border border-violet-200 rounded-md shadow-sm hover:bg-violet-50"
          title="Abrir en pestaña nueva"
        >
          <ExternalLink className="h-3.5 w-3.5 text-violet-600" />
        </a>
      </div>
    </div>
  );
}
